/*
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../app/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI',
	'../util/IconList',
	'underscore',
	'../../../utils/FeatureFlagUtil'
], (StringResources, APIFactory, PropertiesProviderAPI, IconList, _, FeatureFlagUtil) => {
	class PageProperties {
		constructor(options) {
			this.dashboard = options.dashboardAPI;
			this._features = options.features;
			this.content = options.content;

			this._api = APIFactory.createAPI(this, [PropertiesProviderAPI]);
			options.features['Properties'].registerProvider(this._api);


			const addCheck = FeatureFlagUtil.generateChecker(this.dashboard, this);
			// TODO: Remove when PropertiesV2 FF not longer needed
			addCheck('_isPropertiesV2Enabled', ['dashboard', 'PropertiesV2', 'enabled']);
		}

		getAPI() {
			return this._api;
		}

		_isTopLevelPage() {
			return !this.content.getContainer();
		}

		_getSectionId(v1SectionId){
			return this._isPropertiesV2Enabled ? v1SectionId.replace('page.', '') : v1SectionId;
		}

		/*
			This property is used from the TopLevelPropertiesProvider
		*/
		_generateBackgroundColorProperties(propertyList) {
			propertyList.push({
				id: `backgroundColor`,
				editor: {
					hidden: true
				}
			});
		}


		getPropertyList() {
			const propertyList = [];
			this._generateBackgroundColorProperties(propertyList);

			if(this._isTopLevelPage()) {
				return propertyList;
			}

			const restoreMasterStyling = () => {
				const transaction = this._features['Transaction'];
				const transactionToken = transaction.startTransaction();
				this.content.setPropertyValue('tabTextColor', 'transparent', transactionToken);
				this.content.setPropertyValue('tabSelectedLineColor', 'transparent', transactionToken);
				this.content.setPropertyValue('tabBackgroundColor', 'transparent', transactionToken);
				this.content.setPropertyValue('tabIconColor', 'transparent', transactionToken);
				transaction.endTransaction(transactionToken);
				if (!this._isPropertiesV2Enabled) {
					this.dashboard.triggerDashboardEvent('properties:refreshChild', {
						content: this.content,
						focusSelector: '.clickCallback_left_0_tabRestoreDefaults'
					});
				}
			};

			const translationService = this.dashboard.getDashboardCoreSvc('TranslationService');
			const section_id_title_section = this._getSectionId('page.titleSection');
			const section_id_tab_icon_area = this._getSectionId('page.tabIconArea');
			const section_id_title_background = this._getSectionId('page.titleBackground');

			const icons = this._features['Dashboard.Icons'];

			let tabRestoreDefaultsProp;
			if (this._isPropertiesV2Enabled) {
				tabRestoreDefaultsProp =  {
					id: 'tabRestoreDefaults',
					editor: {
						sectionId: section_id_title_background,
						uiControl: {
							type: 'SingleLineLinks',
							justifyContent: 'flex-end',
							alignItems: 'center',
							items: [{
								align: 'left',
								items: [{
									type: 'text',
									class: 'iconTextLink',
									grow: false,
									value: StringResources.get('propTabRestoreDefaults'),
									ariaLabel: StringResources.get('propTabRestoreDefaults'),
									clickCallback: restoreMasterStyling
								}]
							}, {
								align: 'right',
								items: [{
									type: 'icon',
									class: 'iconTextLink',
									svgIcon: icons.getIcon('restore').id,
									iconTooltip: StringResources.get('propTabRestoreDefaults'),
									clickCallback: restoreMasterStyling,
									tooltipPosition: 'left'
								}]
							}]
						}
					}
				};
			} else {
				tabRestoreDefaultsProp =  {
					id: 'tabRestoreDefaults',
					editor: {
						sectionId: section_id_title_background,
						uiControl: {
							type: 'SingleLineLinks',
							items: [{
								align: 'left',
								items: [{
									type: 'icon',
									class: 'iconTextLink',
									svgIcon: icons.getIcon('restore').id,
									iconTooltip: StringResources.get('propTabRestoreDefaults'),
									clickCallback: restoreMasterStyling
								}]
							}, {
								align: 'right',
								items: [{
									type: 'text',
									class: 'iconTextLink',
									value: StringResources.get('propTabRestoreDefaults'),
									ariaLabel: StringResources.get('propTabRestoreDefaults'),
									clickCallback: restoreMasterStyling
								}]
							}]
						}
					}
				};
			}

			propertyList.push(
				{
					id: 'title',
					getPropertyValue: () => {
						const layoutModel = this.getLayoutModel();
						return layoutModel.get('title');
					},
					setPropertyValue: (value) => {
						const layoutModel = this.getLayoutModel();
						if(layoutModel) {
							layoutModel.set({ title: value }, {
								payloadData: { skipUndoRedo: true }
							});
						}
					},
					editor: {
						sectionId: section_id_title_section,
						uiControl: {
							type: 'InputLabel',
							label: StringResources.get('propTabTitle'),
							ariaLabel: StringResources.get('propTabTitle'),
							handleReturnKey: true,
							needsTranslationIcon: () => {
								if (!translationService.isInTranslationMode()) {
									return false;
								}
								const layoutModel = this.getLayoutModel();
								if(layoutModel) {
									return layoutModel.getMultilingualAttribute('title').multilingualProperty.needsTranslation();
								} else {
									return false;
								}
							},
							appendTranslationIcon: translationService.appendTranslationIcon.bind(translationService)
						}
					}
				},
				this._generateColorPickerSpec('tabTextColor', section_id_title_section, 'propTabTextColor'),
				{
					id: 'tabIcon',
					getPropertyValue: () => {
						const layoutModel = this.getLayoutModel();
						const icon = layoutModel.get('tabIcon');
						if(icon && icon !== 'transparent') {
							return icons.getIcon(icon).id;
						} else {
							return 'transparent';
						}
					},
					setPropertyValue: (value) => {
						const layoutModel = this.getLayoutModel();
						if(layoutModel) {
							// Transform string to object if input is string
							const iconList = IconList.getTabIcons(icons, this._isPropertiesV2Enabled);
							if (typeof value === 'string') {
								value = _.find(iconList, (icon) => {
									return icon.value === value;
								});
								value = value || iconList[0]; // the first element is the transparent option
							}
							layoutModel.set({ tabIcon: value.name }, {
								payloadData: { skipUndoRedo: true }
							});
						}
					},
					editor: {
						sectionId: section_id_tab_icon_area,
						uiControl: {
							type: 'CollapsiblePicker',
							label: StringResources.get('propTabIcon'),
							ariaLabel: StringResources.get('propTabIcon'),
							selectedName: this.getLayoutModel().get('tabIcon'),
							placeholder: {
								name: 'transparent',
								label: StringResources.get('no_icon'),
								type: 'string',
								value: 'transparent'
							},
							contentSize: 'small',
							items: IconList.getTabIcons(icons, this._isPropertiesV2Enabled)
						}
					}
				},
				this._generateColorPickerSpec('tabIconColor', section_id_tab_icon_area, 'propTabIconColor'),
				this._generateColorPickerSpec('tabSelectedLineColor', section_id_title_background, 'propTabSelectedBarColor'),
				this._generateColorPickerSpec('tabBackgroundColor', section_id_title_background, 'propTabBackgroundColor'),
				tabRestoreDefaultsProp
			);

			return propertyList;
		}

		getPropertyLayoutList() {
			if(this._isTopLevelPage()) {
				return [];
			}

			const propertyLayoutList = [{
				value: StringResources.get('individualTabProperties'),
				centerLabel: true,
				id: 'banner',
				type: 'Banner',
				backButton: true,
				ariaLabel: StringResources.get('individualTabProperties')
			},  {
				id: 'titleSection',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('sectionName_tabTitle')
			}, {
				id: 'titleBackground',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('sectionName_tabBackground')
			}, {
				id: 'tabIconArea',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('propTabIconLabel')
			}];
			if(!this._isPropertiesV2Enabled){
				propertyLayoutList.splice(1, 0, {
					id: 'page',
					type: 'Group'
				});
			}
			return propertyLayoutList;
		}

		reset() { }

		getLayoutModel() {
			const boardModel = this._features['internal'].getBoardModel();
			return boardModel.layout.findModel(this.content.getId());
		}

		_getPropertyValue(propName) {
			const model = this.getLayoutModel();
			return model.get(propName);
		}

		/**
		 * Prepare a payload and use it to set a property on the model
		 * We **must** treat the propertyValue of "transparent" as a reset to use default values
		 */
		_changeTabColor(propertyName, propertyValue) {
			const payload = {};
			if(!propertyValue || propertyValue === 'transparent') {
				payload[propertyName] = null;
			} else {
				payload[propertyName] = this._features['Colors'].getColorClassName(propertyValue);
			}
			this._features['Colors'].prepareForColorModelChange(payload, propertyName);

			const model = this.getLayoutModel();
			model.set(payload, {
				sender: this.senderId,
				payloadData: { skipUndoRedo: true }
			});
		}

		_generateColorPickerSpec(propertyId, sectionId, labelKey) {
			if (this._isPropertiesV2Enabled) {
				return {
					id: propertyId,
					getPropertyValue: () => this._getPropertyValue(propertyId) || 'transparent',
					setPropertyValue: (value) => this._changeTabColor(propertyId, value),
					editor: {
						sectionId,
						uiControl: {
							type: 'ColorPicker',
							label: StringResources.get(labelKey),
							paletteTitle: StringResources.get('themeLabel'),
							paletteType: 'DashboardColorSet',
							showHexValue: true,
							addButton: true,
						}
					}
				};
			} else {
				return {
					id: propertyId,
					getPropertyValue: () => this._getPropertyValue(propertyId),
					setPropertyValue: (value, transactionToken) => {
						this._changeTabColor(propertyId, value, transactionToken);
					},
					editor: {
						sectionId,
						uiControl: {
							type: 'ColorPicker',
							label: StringResources.get(labelKey),
							ariaLabel: StringResources.get(labelKey),
							paletteType: 'DashboardColorSet',
							open: false,
							addButton: true,
							showHexValue: true,
							onChange: (propertyName, propertyValueInfo = {}) => {
								this.content.setPropertyValue(propertyName, propertyValueInfo.name);
							},
						}
					}
				};
			}
		}

	}
	return PageProperties;
});
